
import React, { memo, useEffect } from 'react';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from 'lucide-react';

interface MediaDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isUploading: boolean;
  previewUrl: string | null;
  hasImageToConfirm: boolean;
  isUsingDataUrl?: boolean;
  bucketError?: string | null;
  requireConfirmation?: boolean;
}

// Utilisation de memo avec une fonction de comparaison personnalisée pour éviter les re-rendus inutiles
const MediaDialog = memo(({
  onFileChange,
  onConfirm,
  onCancel,
  isUploading,
  previewUrl,
  hasImageToConfirm,
  requireConfirmation = true
}: MediaDialogProps) => {
  
  // Suivre le nombre de rendus
  useEffect(() => {
    console.log("[MediaDialog] Rendu avec hasImageToConfirm:", hasImageToConfirm);
  }, [hasImageToConfirm]);
  
  return (
    <DialogContent className="sm:max-w-[450px]">
      <DialogHeader>
        <DialogTitle>Ajouter une image</DialogTitle>
      </DialogHeader>

      {hasImageToConfirm ? (
        <div className="space-y-4">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            <img
              src={previewUrl || ''}
              alt="Prévisualisation"
              className="object-cover w-full h-full"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isUploading}
            >
              Annuler
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isUploading}
            >
              {isUploading ? 'Upload en cours...' : 'Confirmer'}
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => {
            const fileInput = document.getElementById('file-upload') as HTMLInputElement;
            if (fileInput) fileInput.click();
          }}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-spin" />
              <p className="text-sm text-gray-500">Téléchargement en cours...</p>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Cliquez pour parcourir vos fichiers</p>
            </>
          )}
          <input 
            id="file-upload"
            type="file" 
            onChange={onFileChange} 
            className="hidden" 
            accept="image/*"
            disabled={isUploading}
          />
        </div>
      )}
    </DialogContent>
  );
}, (prevProps, nextProps) => {
  // Logique de comparaison personnalisée pour optimiser les re-rendus
  // Ne rendre à nouveau que si certaines props importantes changent
  return (
    prevProps.hasImageToConfirm === nextProps.hasImageToConfirm &&
    prevProps.isUploading === nextProps.isUploading &&
    prevProps.previewUrl === nextProps.previewUrl
  );
});

MediaDialog.displayName = 'MediaDialog';

export default MediaDialog;
